import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box,
  Stack,
  Divider,
  Snackbar,
  Alert,
  Fade,
  Chip,
  keyframes
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MedicalIcon from '../components/MedicalIcon/MedicalIcon';
import { useRTL } from '../context/RTLContext';
import { translations } from '../translations/404';
import styles from './NotFound.module.css';

// Subtle glow animation for primary CTA
const subtleGlow = keyframes`
  0%, 100% {
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.25);
  }
  50% {
    box-shadow: 0 2px 12px rgba(25, 118, 210, 0.35);
  }
`;

// Success animation
const successPop = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const NotFound: React.FC = () => {
  const { language, direction } = useRTL();
  const [reportSent, setReportSent] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [clickedButton, setClickedButton] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const lang = language.split('-')[0] as keyof typeof translations;
  const t = translations[lang] || translations.en;

  React.useEffect(() => {
    // Trigger fade-in animation
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const handleReportIssue = async () => {
    const errorData = {
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
      language: language,
      userAgent: navigator.userAgent,
      referrer: document.referrer
    };
    
    console.log('404 Error reported:', errorData);
    
    // In production, send this to your analytics service
    // try {
    //   await fetch('/api/error-report', {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify(errorData)
    //   });
    // } catch (error) {
    //   console.error('Failed to report error:', error);
    // }
    
    setReportSent(true);
  };


  const handlePrimaryCTA = () => {
    setClickedButton(true);
    setShowSuccess(true);
    setTimeout(() => {
      window.location.href = '/questionnaire/new';
    }, 500);
  };

  const handleSecondaryCTA = () => {
    setClickedButton(true);
    setShowSuccess(true);
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  };

  // Common pathways based on referrer analysis
  const suggestedPaths = [
    { label: t.commonLinks?.newAssessment, href: '/questionnaire/new', onClick: undefined },
    { label: t.commonLinks?.checkStatus, href: '/status', onClick: undefined },
    { label: t.commonLinks?.learnMore, href: '/', onClick: undefined }
  ];

  return (
    <Container maxWidth="sm" className={styles.container}>
      <Fade in={showContent} timeout={600}>
        <Paper className={styles.paper} elevation={0}>
          
          {/* Logo */}
          <Box className={styles.logoContainer}>
            <img 
              src="/favicon.svg" 
              alt="Tibbbi Logo" 
              className={styles.logo}
              aria-label="Tibbbi Healthcare Logo"
            />
          </Box>

          {/* Professional header divider */}
          <Box className={styles.headerDivider} />

          {/* Static Medical Icon */}
          <Box className={styles.iconContainer}>
            <MedicalIcon size={48} />
          </Box>
          
          {/* Main Message */}
          <Typography 
            variant="h4" 
            className={styles.title}
          >
            {t.title}
          </Typography>
          
          <Typography 
            variant="body1" 
            className={styles.subtitle}
          >
            {t.subtitle}
          </Typography>

          {/* Reassurance as subtle inline text */}
          <Typography 
            variant="body2" 
            className={styles.reassuranceText}
          >
            {t.reassurance}
          </Typography>

          {/* Primary Actions - Clear hierarchy */}
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              startIcon={<AssignmentIcon />}
              endIcon={direction === 'rtl' ? null : <ArrowForwardIcon />}
              onClick={handlePrimaryCTA}
              disabled={clickedButton}
              sx={{
                py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 1.5,
                backgroundColor: '#1976d2',
                animation: `${subtleGlow} 2s ease-in-out infinite`,
                '&:hover': {
                  backgroundColor: '#1565c0',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              {t.buttons.newQuestionnaire}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              size="large"
              fullWidth
              startIcon={<HomeIcon />}
              onClick={handleSecondaryCTA}
              disabled={clickedButton}
              sx={{
                py: 1.5,
                fontSize: '0.95rem',
                textTransform: 'none',
                borderRadius: 1.5,
                borderColor: 'rgba(25, 118, 210, 0.3)',
                color: '#546e7a',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  color: '#1976d2'
                }
              }}
            >
              {t.buttons.home}
            </Button>
          </Stack>

          {/* Did You Mean? Suggestions - Interactive chips */}
          {t.didYouMean && (
            <>
              <Divider className={styles.sectionDivider} sx={{ my: 3 }} />
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#78909c',
                    mb: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  {t.didYouMean}
                </Typography>
                <Stack 
                  direction="row" 
                  spacing={1} 
                  flexWrap="wrap" 
                  justifyContent="center"
                  sx={{ gap: 1 }}
                >
                  {suggestedPaths.map((path, index) => (
                    <Chip
                      key={index}
                      label={path.label}
                      component={path.onClick ? "button" : "a"}
                      href={path.onClick ? undefined : path.href}
                      onClick={path.onClick}
                      clickable
                      className={styles.suggestionChip}
                      sx={{
                        fontSize: '0.875rem',
                        height: 32,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Report Issue - Very subtle */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<ReportProblemIcon sx={{ fontSize: 14 }} />}
              onClick={handleReportIssue}
              sx={{
                color: '#b0bec5',
                textTransform: 'none',
                fontSize: '0.8rem',
                '&:hover': {
                  color: '#90a4ae',
                  backgroundColor: 'transparent'
                }
              }}
              aria-label="Report this 404 error to help us improve"
            >
              {t.buttons.reportIssue}
            </Button>
          </Box>
        </Paper>
      </Fade>

      {/* Success Snackbar for error reporting */}
      <Snackbar 
        open={reportSent} 
        autoHideDuration={3000} 
        onClose={() => setReportSent(false)}
        anchorOrigin={{ 
          vertical: 'bottom', 
          horizontal: direction === 'rtl' ? 'left' : 'right' 
        }}
      >
        <Alert 
          onClose={() => setReportSent(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t.reportSent}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NotFound;